var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { createElement, Fragment, useEffect, useRef, useState, useMemo, useCallback, } from 'react';
import { createRoot } from 'react-dom/client';
import { usePagination, useSearchBox } from 'react-instantsearch';
import { autocomplete, getAlgoliaResults, } from '@algolia/autocomplete-js';
import { createLocalStorageRecentSearchesPlugin } from '@algolia/autocomplete-plugin-recent-searches';
import { debounce } from '@algolia/autocomplete-shared';
import debouncePromise from '../../../helpers/debouncePromise';
import AutocompleteItem from '../../../components/searching/autocomplete/AutocompleteItem';
export default function Autocomplete(_a) {
    var { searchClient, defaultIndexName, defaultFilters = undefined, sources, useRecentSearches = true, closeOnBlur = false, recentSearchesOptions = {}, additionalData, onSubmit = undefined } = _a, autocompleteProps = __rest(_a, ["searchClient", "defaultIndexName", "defaultFilters", "sources", "useRecentSearches", "closeOnBlur", "recentSearchesOptions", "additionalData", "onSubmit"]);
    const autocompleteContainer = useRef(null);
    const { query: searchBoxQuery, refine: setSearchBoxQuery } = useSearchBox();
    const { refine: setPage } = usePagination();
    const autocompleteInstance = useRef(null);
    const [instantSearchUiState, setInstantSearchUiState,] = useState({ query: searchBoxQuery });
    useEffect(() => {
        setSearchBoxQuery(instantSearchUiState.query);
        setPage(0);
    }, [instantSearchUiState, setSearchBoxQuery, setPage]);
    const plugins = useMemo(() => {
        const pluginArray = [];
        if (useRecentSearches) {
            pluginArray.push(createLocalStorageRecentSearchesPlugin(Object.assign(Object.assign({ key: 'instantsearch', limit: 3 }, recentSearchesOptions), { transformSource({ source }) {
                    return Object.assign(Object.assign({}, source), { onSelect({ item }) {
                            setInstantSearchUiState({ query: item.label });
                        } });
                } })));
        }
        if (sources.length > 0) {
            const debouncedSources = debouncePromise((items) => Promise.resolve(items), 350);
            pluginArray.push({
                name: 'aa.querySuggestionsPlugin',
                getSources({ query, setQuery, refresh }) {
                    if (!query) {
                        return [];
                    }
                    return debouncedSources(sources.map((sourceDef, key) => {
                        var _a, _b, _c, _d;
                        const source = {
                            sourceId: sourceDef.sourceId || `querySuggestionsPlugin_${key}`,
                            getItemInputValue: sourceDef.getItemInputValue
                                || (({ item }) => item.title),
                            getItemUrl: sourceDef.getItemUrl
                                || (({ item }) => item.url),
                            getItems() {
                                // Using searchkit to get results for the query
                                return getAlgoliaResults({
                                    searchClient,
                                    queries: [
                                        {
                                            indexName: sourceDef.indexName || defaultIndexName,
                                            query,
                                            params: (sourceDef.getQueryParams
                                                || ((q, f) => ({ query: q, filters: f })))(query, defaultFilters),
                                        },
                                    ],
                                    transformResponse: sourceDef.transformResponse
                                        || (({ hits }) => (hits[0])),
                                });
                            },
                            templates: {},
                            onFillQuery({ item, state }) {
                                setQuery(source.getItemInputValue({ item, state }));
                                refresh();
                            },
                        };
                        if (((_a = sourceDef.templates) === null || _a === void 0 ? void 0 : _a.item) !== undefined) {
                            source.templates.item = (params) => {
                                var _a;
                                return (_a = sourceDef.templates) === null || _a === void 0 ? void 0 : _a.item(Object.assign(Object.assign({}, params), { source,
                                    additionalData }));
                            };
                        }
                        else {
                            source.templates.item = ({ item, components, state }) => (React.createElement(AutocompleteItem, { hit: item, url: source.getItemUrl({ item, state }), components: components, onFillQuery: () => source.onFillQuery({ item, state }) }));
                        }
                        if (((_b = sourceDef.templates) === null || _b === void 0 ? void 0 : _b.header) !== undefined) {
                            source.templates.header = (params) => {
                                var _a;
                                return (_a = sourceDef.templates) === null || _a === void 0 ? void 0 : _a.header(Object.assign(Object.assign({}, params), { source,
                                    additionalData }));
                            };
                        }
                        if (((_c = sourceDef.templates) === null || _c === void 0 ? void 0 : _c.footer) !== undefined) {
                            source.templates.footer = (params) => {
                                var _a;
                                return (_a = sourceDef.templates) === null || _a === void 0 ? void 0 : _a.footer(Object.assign(Object.assign({}, params), { source,
                                    additionalData }));
                            };
                        }
                        if (((_d = sourceDef.templates) === null || _d === void 0 ? void 0 : _d.noResults) !== undefined) {
                            source.templates.noResults = (params) => {
                                var _a;
                                return (_a = sourceDef.templates) === null || _a === void 0 ? void 0 : _a.noResults(Object.assign(Object.assign({}, params), { source,
                                    additionalData }));
                            };
                        }
                        return source;
                    }));
                },
            });
        }
        return pluginArray;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const panelRoot = useRef(null);
    useEffect(() => {
        if (!autocompleteContainer.current) {
            return;
        }
        const debouncedSetInstantSearchUiState = debounce(setInstantSearchUiState, 500);
        autocompleteInstance.current = autocomplete(Object.assign(Object.assign({}, autocompleteProps), { container: autocompleteContainer.current, initialState: { query: searchBoxQuery }, onReset() {
                setInstantSearchUiState({ query: '' });
            },
            onSubmit(params) {
                setInstantSearchUiState({ query: params.state.query });
                if (onSubmit) {
                    onSubmit(params);
                }
            },
            onStateChange({ prevState, state }) {
                if (prevState.query !== state.query) {
                    debouncedSetInstantSearchUiState({
                        query: state.query,
                    });
                }
            }, renderer: {
                createElement,
                Fragment,
                render: () => { },
            }, render({ children }, root) {
                if (!panelRoot.current) {
                    panelRoot.current = createRoot(root);
                }
                panelRoot.current.render(children);
            }, plugins: plugins }));
        // eslint-disable-next-line consistent-return
        return () => {
            var _a;
            (_a = autocompleteInstance.current) === null || _a === void 0 ? void 0 : _a.destroy();
            autocompleteInstance.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [plugins]);
    const handleBlur = useCallback(() => {
        var _a;
        if (closeOnBlur) {
            (_a = autocompleteInstance.current) === null || _a === void 0 ? void 0 : _a.setIsOpen(false);
        }
    }, [closeOnBlur]);
    return React.createElement("div", { ref: autocompleteContainer, onBlur: handleBlur });
}
